import { useEffect, useState } from 'react';
import { Form, Alert } from 'react-bootstrap';
import type { AiModel, Topic } from '../types';
import { parsePromptLines } from '../utils/prompt-lines';
import { ModelMultiPicker } from './ModelIcon';

export interface NewPromptFormValues {
  texts: string[];
  modelIds: string[];
  topicId?: string;
}

export interface UseNewPromptFormOptions {
  topics: Topic[];
  models: AiModel[];
  defaultModelId: string;
  defaultTopicId?: string | null;
  /** When this becomes true (or changes while true), form fields reset. */
  active: boolean;
}

export function useNewPromptForm({
  topics,
  models,
  defaultModelId,
  defaultTopicId,
  active,
}: UseNewPromptFormOptions) {
  const [text, setText] = useState('');
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [topicId, setTopicId] = useState('');

  const userTopics = topics.filter((t) => !t.isSystem);
  const promptLines = parsePromptLines(text);
  const canSubmit = Boolean(promptLines.length > 0 && modelIds.length > 0 && models.length > 0);

  const missingHint =
    promptLines.length > 0 && modelIds.length === 0 ? 'حداقل یک مدل AI انتخاب کنید' : null;

  useEffect(() => {
    if (!active) return;
    setText('');
    const initialModelId = defaultModelId || models[0]?.id || '';
    setModelIds(initialModelId ? [initialModelId] : []);
    setTopicId(defaultTopicId ?? '');
  }, [active, defaultModelId, defaultTopicId, models]);

  function getValues(): NewPromptFormValues | null {
    if (!canSubmit) return null;
    return {
      texts: promptLines,
      modelIds,
      topicId: topicId || undefined,
    };
  }

  return {
    text,
    setText,
    modelIds,
    setModelIds,
    topicId,
    setTopicId,
    userTopics,
    models,
    promptLines,
    canSubmit,
    missingHint,
    getValues,
  };
}

export type NewPromptFormState = ReturnType<typeof useNewPromptForm>;

interface NewPromptFieldsProps {
  form: NewPromptFormState;
  formId: string;
  autoFocus?: boolean;
  showModelSearch?: boolean;
  promptHelp?: string;
  onTextKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
}

export function NewPromptFields({
  form,
  formId,
  autoFocus,
  showModelSearch = true,
  promptHelp,
  onTextKeyDown,
  onSubmit,
}: NewPromptFieldsProps) {
  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="d-flex flex-column gap-4"
    >
      <Form.Group>
        <Form.Label className="fs--1 fw-medium">پرامپت</Form.Label>
        <Form.Control
          as="textarea"
          value={form.text}
          onChange={(e) => form.setText(e.target.value)}
          onKeyDown={onTextKeyDown}
          rows={5}
          required
          autoFocus={autoFocus}
          placeholder={
            'بهترین ابزارهای SEO در ۲۰۲۴ کدامند؟\nبهترین ابزار سئو ایرانی نام ببر\nبهترین ابزار برای زبان فارسی چی هست'
          }
          style={{ resize: 'none' }}
        />
        <Form.Text className="text-500">
          {`${promptHelp ?? 'هر خط یک پرامپت جداگانه است'}${
            form.promptLines.length > 0 ? ` — ${form.promptLines.length} پرامپت` : ''
          }.`}
        </Form.Text>
      </Form.Group>

      <fieldset>
        <legend className="fs--1 fw-medium col-form-label pt-0">مدل‌های AI</legend>
        <p className="fs--2 text-500 mt-n2 mb-2">
          مدل‌هایی را که می‌خواهید اجرا شوند انتخاب کنید.
        </p>
        {form.models.length > 0 ? (
          <ModelMultiPicker
            models={form.models}
            value={form.modelIds}
            onChange={form.setModelIds}
            showToolbar
            showSearch={showModelSearch}
            showSelectedBadges={false}
          />
        ) : (
          <Alert variant="warning" className="fs--1 mb-0">
            مدلی در دسترس نیست. از ادمین بخواهید ابتدا مدل‌های AI را فعال کند.
          </Alert>
        )}
      </fieldset>

      {form.userTopics.length > 0 && (
        <Form.Group>
          <Form.Label className="fs--1 fw-medium">
            تاپیک <span className="fs--2 fw-normal text-500">(اختیاری)</span>
          </Form.Label>
          <Form.Select value={form.topicId} onChange={(e) => form.setTopicId(e.target.value)}>
            <option value="">اجراهای سریع</option>
            {form.userTopics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
    </Form>
  );
}
